import type { DesignCompositionRelease } from "../composition/types"
import shardProjection from "./fixtures/design-binding.v1.json"
import libraryBaselineRelease from "./fixtures/library-baseline.release.json"
import type { DesignOntologyReleaseResolver } from "./types"

/**
 * A binding record declared by the L1 Design Ontology binding shard
 * (`packages/cogo-kg-browser/fixtures/design-binding-v1.ttl`).
 */
export interface DesignBindingShardRecord {
  applicationRef: string
  designOntologyReleaseRef: string
  requiredTokenRefs: string[]
}

/**
 * Deterministic projection of the L1 binding shard. TTL ↔ projection parity is
 * enforced by `packages/cogo-kg-browser/__tests__/design-binding-v1.test.ts`,
 * so this file is a checked-in read of the shard, not a second authority.
 */
export interface DesignBindingShardProjection {
  schemaVersion: "cogo.design-ontology-binding-shard-projection.v1"
  shardRef: string
  shapesRef: string
  applicationArtifactSchemaVersion: string
  gapCodes: string[]
  bindings: DesignBindingShardRecord[]
}

export const DESIGN_BINDING_SHARD_PROJECTION =
  shardProjection as DesignBindingShardProjection

/**
 * The Design Ontology releases the L1 shard declares, keyed by the exact
 * `designOntologyReleaseRef` string the compiler is driven with.
 */
const SHARD_DECLARED_RELEASES: readonly DesignCompositionRelease[] = [
  libraryBaselineRelease as DesignCompositionRelease,
]

export function findDesignBindingShardRecord(
  applicationRef: string,
): DesignBindingShardRecord | null {
  const matches = DESIGN_BINDING_SHARD_PROJECTION.bindings.filter(
    (binding) => binding.applicationRef === applicationRef,
  )
  return matches.length === 1 ? structuredClone(matches[0]) : null
}

/**
 * Fail-closed resolver over the L1 shard: it answers ONLY for release refs the
 * shard declares as a binding target. Anything else resolves to `null`, which
 * the compiler turns into `design-ontology-release-not-found`.
 */
export function createShardBackedDesignOntologyReleaseResolver(): DesignOntologyReleaseResolver {
  const declaredRefs = new Set(
    DESIGN_BINDING_SHARD_PROJECTION.bindings.map(
      (binding) => binding.designOntologyReleaseRef,
    ),
  )
  return {
    resolveDesignOntologyRelease: async (ref: string) => {
      if (!declaredRefs.has(ref)) return null
      const matches = SHARD_DECLARED_RELEASES.filter(
        (release) => release.releaseId === ref,
      )
      return matches.length === 1 ? structuredClone(matches[0]) : null
    },
  }
}
